import { useState, useEffect, useCallback, useRef } from 'react'
import { UserService } from '../services/users'
import { AuthService } from '../services/auth'
import { SchoolService } from '../services/schools'
import type { User } from '../services/users'
import { formatNumber } from '../utils/formatNumber'
import { normalizeGrade, normalizeClassName, normalizeAttendanceNumber } from '../utils/gradeClass'
import { scrollToElementTop } from '../utils/scrollToElement'
import { parseCsvImport } from '../utils/csvImport'
import RosterFilterBar from './teacher/RosterFilterBar'
import type { RosterFilterValue } from './teacher/RosterFilterBar'

interface StudentFormData {
  email: string
  name: string
  password: string
  school_id: string
  grade: string
  class: string
  attendance_number: string
}

interface School {
  id: string
  name: string
}

interface StudentManagerProps {
  schoolId?: string
}

const StudentManager = ({ schoolId }: StudentManagerProps = {}) => {
  const [students, setStudents] = useState<User[]>([])
  const [schools, setSchools] = useState<School[]>([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [showCSVImport, setShowCSVImport] = useState(false)
  const [editingStudent, setEditingStudent] = useState<User | null>(null)
  const [formData, setFormData] = useState<StudentFormData>({
    email: '',
    name: '',
    password: '',
    school_id: '',
    grade: '',
    class: '',
    attendance_number: ''
  })
  const [csvData, setCsvData] = useState('')
  const [filter, setFilter] = useState({
    search: '',
    school_id: '',
    grade: '',
    class: '',
    groupId: ''
  })
  // 管理者で学校が未選択のときの自由入力欄（学校が決まっていれば RosterFilterBar 側でデバウンスする）
  const [searchInput, setSearchInput] = useState('')
  const [classInput, setClassInput] = useState('')
  const filterSchoolId = schoolId || filter.school_id
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  const PAGE_SIZE = 100

  const loadStudents = useCallback(async () => {
    try {
      setIsLoading(true)
      const filterParams = {
        role: 'student',
        search: filter.search || undefined,
        schoolId: filterSchoolId || undefined,
        grade: filter.grade || undefined,
        class: filter.class || undefined,
        // 学校が決まっていればクラスは学校の選択肢から選んだ値（完全一致）、未選択なら自由入力（前方一致）
        classMatch: filterSchoolId ? 'exact' as const : 'prefix' as const,
        groupId: filter.groupId || undefined
      }
      const [studentData, count] = await Promise.all([
        UserService.getUsers({
          ...filterParams,
          orderBy: 'roster',
          limit: PAGE_SIZE,
          offset: currentPage * PAGE_SIZE
        }),
        UserService.getUsersCount(filterParams)
      ])
      setStudents(studentData)
      setTotalCount(count)

      // 削除やインポートで件数が減り、現在のページが範囲外になったときは先頭へ戻す
      if (currentPage > 0 && studentData.length === 0 && count > 0) {
        setCurrentPage(0)
      }
    } catch (error) {
      console.error('Failed to load students:', error)
      setError('生徒の読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [filter, filterSchoolId, currentPage])

  useEffect(() => {
    if (filterSchoolId) return
    const timer = setTimeout(() => {
      setFilter(prev =>
        prev.search === searchInput && prev.class === classInput
          ? prev
          : { ...prev, search: searchInput, class: classInput }
      )
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput, classInput, filterSchoolId])

  const rosterFilter: RosterFilterValue = {
    grade: filter.grade || null,
    class: filter.class || null,
    groupId: filter.groupId || null,
    search: filter.search
  }

  const handleRosterFilterChange = useCallback((value: RosterFilterValue) => {
    setFilter(prev => ({
      ...prev,
      grade: value.grade ?? '',
      class: value.class ?? '',
      groupId: value.groupId ?? '',
      search: value.search
    }))
  }, [])

  useEffect(() => {
    setCurrentPage(0)
  }, [filter, schoolId])

  useEffect(() => {
    loadStudents()
    if (!schoolId) {
      loadSchools()
    }
  }, [loadStudents, schoolId])

  const loadSchools = async () => {
    try {
      const schoolData = await SchoolService.getSchools()
      setSchools(schoolData)
    } catch (error) {
      console.error('Failed to load schools:', error)
    }
  }

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      school_id: schoolId || '',
      grade: '',
      class: '',
      attendance_number: ''
    })
    setEditingStudent(null)
    setError('')
    setSuccess('')
    setShowPassword(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setIsLoading(true)

    // 数字だけで入力された学年・クラスには「年」「組」を付けて表記を揃える
    const grade = normalizeGrade(formData.grade)
    const className = normalizeClassName(formData.class)
    const attendanceNumber = normalizeAttendanceNumber(formData.attendance_number)

    try {
      if (editingStudent) {
        // Update existing student
        await UserService.updateUser(editingStudent.id, {
          email: formData.email,
          name: formData.name,
          school_id: formData.school_id,
          grade,
          class: className,
          attendance_number: attendanceNumber || null
        })
        setSuccess('生徒情報を更新しました')
      } else {
        // Create new student
        const result = await AuthService.createStudentAccount({
          email: formData.email,
          name: formData.name,
          password: formData.password,
          school_id: formData.school_id,
          grade,
          class: className,
          attendance_number: attendanceNumber || undefined
        })
        
        if (result.auth_user_id) {
          setSuccess('生徒アカウントを作成しました（即時ログイン可能）')
        } else {
          setSuccess('生徒アカウントを作成しました（メール確認が必要な場合があります）')
        }
      }

      await loadStudents()
      resetForm()
      setShowAddForm(false)
    } catch (error: unknown) {
      console.error('Student creation error:', error)
      
      let errorMessage = '生徒の作成/更新に失敗しました'
      
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (typeof error === 'object' && error !== null) {
        // Handle Supabase error objects
        const err = error as { message?: string; error_description?: string; details?: string }
        if (err.message) {
          errorMessage = err.message
        } else if (err.error_description) {
          errorMessage = err.error_description
        } else if (err.details) {
          errorMessage = err.details
        } else {
          errorMessage = `エラーが発生しました: ${JSON.stringify(error)}`
        }
      } else {
        errorMessage = String(error)
      }
      
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleEdit = (student: User) => {
    setEditingStudent(student)
    setFormData({
      email: student.email,
      name: student.name,
      password: '',
      school_id: student.school_id || '',
      grade: student.grade || '',
      class: student.class || '',
      attendance_number: student.attendance_number || ''
    })
    setError('') // Clear any previous errors
    setSuccess('') // Clear any previous success messages
    setShowAddForm(true)
  }

  const handleDelete = async (studentId: string) => {
    if (!confirm('この生徒を削除しますか？')) return

    try {
      setIsLoading(true)
      await UserService.deleteUser(studentId)
      setSuccess('生徒を削除しました')
      await loadStudents()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || '生徒の削除に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async (studentId: string) => {
    const newPassword = prompt('新しいパスワードを入力してください：')
    if (!newPassword) return

    try {
      setIsLoading(true)
      setError('') // Clear previous errors
      setSuccess('') // Clear previous success messages

      const student = students.find(s => s.id === studentId)
      if (student) {
        await AuthService.setUserPassword(student.id, newPassword)
        setSuccess(`${student.name} のパスワードを更新しました。`)
      }
    } catch (error: unknown) {
      console.error('Password update error:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || 'パスワードのリセットに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  // CSV一括登録の列順（見出し行を省略したときはこの順で読む）
  const studentCsvColumns = schoolId
    ? ['email', 'name', 'password', 'grade', 'class', 'attendance_number']
    : ['email', 'name', 'password', 'school_id', 'grade', 'class', 'attendance_number']

  const handleCSVImport = async () => {
    try {
      setIsLoading(true)
      setError('')
      
      // school_id is auto-filled from context when provided
      const rows = parseCsvImport(csvData, studentCsvColumns)

      let successCount = 0
      const errors: string[] = []

      for (const { lineNumber, data: studentData } of rows) {
        try {
          await AuthService.createStudentAccount({
            email: studentData.email,
            name: studentData.name,
            password: studentData.password,
            school_id: schoolId || studentData.school_id,
            // 「1」「2」のように数字だけで書かれていたら「1年」「2組」に揃える
            grade: normalizeGrade(studentData.grade),
            class: normalizeClassName(studentData.class),
            attendance_number: normalizeAttendanceNumber(studentData.attendance_number) || undefined
          })
          successCount++
        } catch (error) {
          errors.push(`${lineNumber}行目: ${error instanceof Error ? error.message : String(error)}`)
        }
      }

      if (errors.length > 0) {
        setError(`${successCount}件成功、${errors.length}件失敗:\n${errors.join('\n')}`)
      } else {
        setSuccess(`${successCount}件の生徒アカウントを作成しました`)
      }

      setCsvData('')
      setShowCSVImport(false)
      await loadStudents()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || 'CSVインポートに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const grades = ['1年', '2年', '3年', '4年', '5年', '6年']

  // ページ切り替え後は一覧の先頭まで戻す（下部のボタンから押しても位置が迷子にならないように）
  const changePage = (page: number) => {
    const lastPage = Math.max(0, Math.ceil(totalCount / PAGE_SIZE) - 1)
    if (page < 0 || page > lastPage || page === currentPage) return
    setCurrentPage(page)
    requestAnimationFrame(() => scrollToElementTop(listRef.current))
  }

  const renderPagination = () => {
    if (totalCount <= PAGE_SIZE) return null

    return (
      <div className="flex items-center space-x-2 text-sm">
        <span className="text-gray-500">
          {currentPage * PAGE_SIZE + 1}〜{Math.min((currentPage + 1) * PAGE_SIZE, totalCount)}件
        </span>
        <button
          onClick={() => changePage(currentPage - 1)}
          disabled={currentPage === 0}
          className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
        >
          ←前
        </button>
        <button
          onClick={() => changePage(currentPage + 1)}
          disabled={(currentPage + 1) * PAGE_SIZE >= totalCount}
          className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
        >
          次→
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">生徒管理</h2>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              setError('')
              setSuccess('')
              setShowCSVImport(true)
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
          >
            CSV一括登録
          </button>
          <button
            onClick={() => {
              resetForm()
              setShowAddForm(true)
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
          >
            生徒追加
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 space-y-4">
        {!schoolId && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">学校</label>
            <select
              value={filter.school_id}
              onChange={(e) => {
                // 学年・クラス・グループの選択肢は学校ごとなので、学校を変えたらリセットする
                setClassInput('')
                setSearchInput(filter.search)
                setFilter({ ...filter, school_id: e.target.value, grade: '', class: '', groupId: '' })
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">全て</option>
              {schools.map(school => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
          </div>
        )}
        {filterSchoolId ? (
          <RosterFilterBar
            schoolId={filterSchoolId}
            value={rosterFilter}
            onChange={handleRosterFilterChange}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                検索
              </label>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="名前・メールアドレス"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                学年
              </label>
              <select
                value={filter.grade}
                onChange={(e) => setFilter({...filter, grade: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">全て</option>
                {grades.map(grade => (
                  <option key={grade} value={grade}>{grade}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                クラス
              </label>
              <input
                type="text"
                value={classInput}
                onChange={(e) => setClassInput(e.target.value)}
                placeholder="クラス名で検索"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>
        )}
      </div>

      {/* Student List */}
      <div ref={listRef} className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-medium text-gray-900">
              生徒一覧 ({formatNumber(totalCount)}人)
            </h3>
            {renderPagination()}
          </div>

          {/* Success and Error Messages */}
          {success && (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-md p-3">
              <div className="text-green-800 text-sm">{success}</div>
            </div>
          )}
          {error && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3">
              <div className="text-red-800 text-sm whitespace-pre-line">{error}</div>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {!schoolId && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ユーザーID
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  生徒情報
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {schoolId ? '学年・クラス' : '学校・学年・クラス'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  状態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {students.map((student) => (
                <tr key={student.id} className="hover:bg-gray-50">
                  {!schoolId && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <span className="text-xs font-mono text-gray-600" title={student.id}>
                          {student.id.substring(0, 8)}...
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(student.id)
                            setSuccess('IDをコピーしました')
                            setTimeout(() => setSuccess(''), 2000)
                          }}
                          className="text-gray-400 hover:text-gray-600"
                          title="IDをコピー"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{student.name}</div>
                      <div className="text-sm text-gray-500">{student.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {!schoolId && (
                      <div className="text-sm text-gray-900">
                        {schools.find(s => s.id === student.school_id)?.name || '未設定'}
                      </div>
                    )}
                    <div className="text-sm text-gray-500">
                      {student.grade} {student.class}
                      {student.attendance_number && ` (${student.attendance_number}番)`}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        student.password_reset_required 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {student.password_reset_required ? 'パスワード要変更' : 'アクティブ'}
                      </span>
                      {student.last_login && (
                        <span className="text-xs text-gray-500">
                          最終: {new Date(student.last_login).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(student)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleResetPassword(student.id)}
                      className="text-yellow-600 hover:text-yellow-900"
                    >
                      パスワードリセット
                    </button>
                    <button
                      onClick={() => handleDelete(student.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination (bottom) */}
        {totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200">
            {renderPagination()}
          </div>
        )}
      </div>

      {/* Add/Edit Student Form */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingStudent ? '生徒情報編集' : '新規生徒追加'}
            </h3>
            
            {/* Error/Success Messages in Form */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-red-800 text-sm">{error}</div>
              </div>
            )}
            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="text-green-800 text-sm">{success}</div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  メールアドレス
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  名前
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
              {!editingStudent && (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      パスワード
                    </label>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, password: 'Mirapass2025'})}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      デフォルトパスワードを使用
                    </button>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                  <label className="flex items-center mt-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showPassword}
                      onChange={(e) => setShowPassword(e.target.checked)}
                      className="mr-2"
                    />
                    パスワードを表示
                  </label>
                </div>
              )}
              {!schoolId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    学校
                  </label>
                  <select
                    value={formData.school_id}
                    onChange={(e) => setFormData({...formData, school_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">選択してください</option>
                    {schools.map(school => (
                      <option key={school.id} value={school.id}>{school.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    学年
                  </label>
                  <select
                    value={formData.grade}
                    onChange={(e) => setFormData({...formData, grade: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">選択</option>
                    {grades.map(grade => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    クラス
                  </label>
                  <input
                    type="text"
                    value={formData.class}
                    onChange={(e) => setFormData({...formData, class: e.target.value})}
                    placeholder="クラス名を入力"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  出席番号
                </label>
                <input
                  type="text"
                  value={formData.attendance_number}
                  onChange={(e) => setFormData({...formData, attendance_number: e.target.value})}
                  placeholder="出席番号（任意）"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                >
                  {isLoading ? '処理中...' : (editingStudent ? '更新' : '作成')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false)
                    resetForm()
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg font-medium"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCSVImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              CSV一括登録
            </h3>
            
            {/* Error/Success Messages in CSV Import Modal */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-red-800 text-sm whitespace-pre-line">{error}</div>
              </div>
            )}
            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="text-green-800 text-sm">{success}</div>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CSVデータ
                </label>
                <div className="text-sm text-gray-600 mb-2">
                  形式: {studentCsvColumns.join(',')}（1行目の見出しは省略可）
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  学年・クラスは数字だけでも登録できます（例: 1, 2 → 「1年」「2組」に自動変換）。
                </div>
                <textarea
                  value={csvData}
                  onChange={(e) => setCsvData(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 h-40"
                  placeholder={schoolId
                    ? "student1@example.com,山田太郎,password123,1年,A組,1"
                    : "student1@example.com,山田太郎,password123,school-id,1年,A組,1"}
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleCSVImport}
                  disabled={isLoading || !csvData.trim()}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                >
                  {isLoading ? '処理中...' : 'インポート'}
                </button>
                <button
                  onClick={() => {
                    setShowCSVImport(false)
                    setCsvData('')
                    setError('')
                    setSuccess('')
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg font-medium"
                >
                  キャンセル
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StudentManager